import { makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { TFilter } from './types';
import {
  getResponsiveTableContext,
  useResponsiveTableContext,
} from '@apia/table';
import { arrayOrArray } from '@apia/util';
import { RangeFilterRenderer } from './renderers/RangeFilterRenderer';
import { SelectFilterRenderer } from './renderers/SelectFilterRenderer';
import { InputFilterRenderer } from './renderers/InputFilterRenderer';
import { DateFilterRenderer } from './renderers/DateFilterRenderer';
import { FreeLabelFilterRenderer } from './renderers/FreeLabelFilterRenderer';

export class Filter {
  protected tableName?: string = undefined;

  filterState: TFilter = {
    runAutomatically: false,
    value: '',
  } as TFilter;

  focus() {}

  constructor(properties: TFilter) {
    Object.assign(this.filterState, properties);

    makeObservable(this, { filterState: observable });

    reaction(
      () => this.filterState.value,
      () => {
        if (this.tableName) {
          getResponsiveTableContext(this.tableName).onFilterChange?.({
            id: properties.id,
            column: properties.column,
          } as any);
        }
      },
    );

    reaction(
      () => this.filterState.filterToValue,
      () => {
        if (this.tableName) {
          getResponsiveTableContext(this.tableName).onFilterChange?.({
            id: properties.id,
            column: properties.column,
          } as any);
        }
      },
    );

    reaction(
      () => this.filterState.multiplier,
      () => {
        if (this.tableName) {
          getResponsiveTableContext(this.tableName).onFilterChange?.({
            id: properties.id,
            column: properties.column,
          } as any);
        }
      },
    );
  }

  getInputRenderer() {
    return <InputFilterRenderer state={this.filterState} />;
  }

  getSelectRenderer() {
    return <SelectFilterRenderer state={this.filterState} />;
  }

  getRangeRenderer() {
    return <RangeFilterRenderer state={this.filterState} />;
  }

  getDateRenderer() {
    return <DateFilterRenderer state={this.filterState} />;
  }

  getDoubleRenderer() {
    return <FreeLabelFilterRenderer state={this.filterState} />;
  }

  Component = observer(() => {
    const { name } = useResponsiveTableContext();
    this.tableName = name;

    if (this.filterState.isRange) {
      return this.getRangeRenderer();
    }

    if (arrayOrArray(this.filterState.options).length > 0) {
      return this.getSelectRenderer();
    }

    if (this.filterState.type === 'D' || this.filterState.type === 'date') {
      return this.getDateRenderer();
    }

    if (this.filterState.type === 'double') {
      return this.getDoubleRenderer();
    }

    return this.getInputRenderer();
  });
}
